<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { onMount } from 'svelte';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { getAgent, getAgentName, getAgentSelectOptions } from './agents/agents.config';
	import type { AgentId } from './agents/agents.types';

	export let disabled = false;

	const dispatch = createEventDispatcher<{ refocus: void }>();
	const options = getAgentSelectOptions();

	// Get dynamic version from health state, fallback to config version
	$: activeAgentId = $aiSceneChatStore.activeAgentId;
	$: activeAgent = getAgent(activeAgentId);
	$: displayVersion = $aiSceneChatStore?.agentHealth?.[activeAgentId]?.version || activeAgent.version;

	let open = false;
	let selectorEl: HTMLDivElement;

	function toggle() {
		if (disabled) return;
		open = !open;
		dispatch('refocus');
	}

	function select(value: AgentId) {
		aiSceneChatStore.setActiveAgent(value);
		open = false;
		dispatch('refocus');
	}

	function handleClickOutside(event: MouseEvent) {
		if (selectorEl && !selectorEl.contains(event.target as Node)) {
			open = false;
		}
	}

	onMount(() => {
		setTimeout(() => {
			document.addEventListener('click', handleClickOutside);
		}, 0);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});
</script>

<div class="agent-selector" bind:this={selectorEl} on:mousedown|preventDefault role="presentation">
	<button class="selector-btn" on:click={toggle} type="button" {disabled}>
		<i class="fa-solid fa-sparkles gradient-icon" aria-hidden="true"></i>
		<span>{activeAgent.name} {displayVersion}</span>
		<i class="fa-regular fa-chevron-down chevron" aria-hidden="true"></i>
	</button>

	{#if open}
		<div class="dropdown">
			{#each options as opt}
				<button
					class="dropdown-item"
					class:active={$aiSceneChatStore.activeAgentId === opt.value}
					on:click={() => select(opt.value)}
					type="button"
				>
					{getAgent(opt.value).name}
					{$aiSceneChatStore?.agentHealth?.[opt.value]?.version || getAgent(opt.value).version}
				</button>
			{/each}
		</div>
	{/if}
</div>

<style>
	.agent-selector {
		position: relative;
	}

	.selector-btn {
		display: flex;
		align-items: center;
		gap: 6px;
		height: 32px;
		padding: 6px;
		background: transparent;
		border: none;
		border-radius: 8px;
		color: var(--color-text-secondary, #888);
		font-family: 'Inter', sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: 16px;
		cursor: pointer;
		transition: background-color 0.2s;
		white-space: nowrap;
	}

	.selector-btn:hover:not(:disabled) {
		background: var(--color-background-2, #242527);
		color: var(--color-text-primary, #eaeaea);
	}

	.selector-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.selector-btn i {
		font-size: 13px;
		width: 16px;
		height: 16px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* AI Gradient Icon */
	.gradient-icon {
		background: var(--Default-Gradients-AI, linear-gradient(255deg, #0f8 0%, #4f48ff 100%));
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}

	.chevron {
		font-size: 12px !important;
		color: var(--color-text-secondary, #888);
	}

	.dropdown {
		position: absolute;
		bottom: calc(100% + 4px);
		left: 0;
		min-width: 140px;
		background: var(--color-background-1, #151617);
		border: 1px solid var(--color-border-2, #5b5f66);
		border-radius: 8px;
		box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
		z-index: 10;
		overflow: hidden;
	}

	.dropdown-item {
		display: block;
		width: 100%;
		padding: 8px 12px;
		background: transparent;
		border: none;
		color: var(--color-text-primary, #eaeaea);
		font-family: 'Inter', sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: 16px;
		text-align: left;
		cursor: pointer;
		transition: background-color 0.15s;
	}

	.dropdown-item:hover,
	.dropdown-item.active {
		background: var(--color-background-2, #242527);
	}

	.dropdown-item.active {
		color: var(--color-primary-1);
	}
</style>
